import * as React from 'react'

import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import {
  FileCategory,
  IUploadLog,
  SitePermitStatus,
  SourceEntityType,
} from '~/client/graph'
import {
  IColumnConfiguration,
  IUploadLogConfiguration,
} from '~/client/graph/types.generated'
import { IDateFilters } from '~/client/src/desktop/components/Filters/DateSelector/DateSelector.store'
import { TwoMonthsDatePickerMode } from '~/client/src/desktop/components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import ViewModes from '~/client/src/desktop/enums/ViewModes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import DeliveryDetailsStore from '~/client/src/shared/components/DeliveryDetails/DeliveryDetails.store'
import * as Icons from '~/client/src/shared/components/Icons'
import {
  ILWFCCategory,
  ILWFCColumn,
  ILWFCRow,
} from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import {
  AttachmentGroupingOption,
  AttachmentsSourceGroupingOption,
} from '~/client/src/shared/enums/AttachmentsGroupingOption'
import { AttachmentSortingOption } from '~/client/src/shared/enums/AttachmentsSortingOption'
import FileType from '~/client/src/shared/enums/FileType'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { getFormStatusDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import UserProject from '~/client/src/shared/models/UserProject'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UploadLogConfigurationStore from '~/client/src/shared/stores/domain/UploadLogConfiguration.store'
import UploadLogsStore from '~/client/src/shared/stores/domain/UploadLogs.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import {
  CATEGORY_ROW_HEIGHT,
  ITreeNodeObj,
} from '~/client/src/shared/stores/ui/BaseList.store'
import BaseMultiBandListStore from '~/client/src/shared/stores/ui/BaseMultiBandList.store'
import ProjectDateStore, {
  isBetween,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import Guard from '~/client/src/shared/utils/Guard'
import { groupingCommonMapper } from '~/client/src/shared/utils/mappers'
import { NO_SPECIFIED } from '~/client/src/shared/utils/usefulStrings'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import {
  getModeSettings,
  IModeSettings,
} from '../../components/HeaderBar/components/ViewModeToggle/ViewModeToggle'
import { IViewState } from '../../interfaces/IViewState'
import FileCategoryIcon from './components/AttachmentsList/FileCategoryIcon'
import FileTypeIcon from './components/AttachmentsList/FileTypeIcon'

/**
 * Converts a timestamp to a day-based key (YYYY-MM-DD format)
 * @param timestamp - The timestamp in milliseconds
 * @returns A string in YYYY-MM-DD format or null if invalid
 */
function getDateKey(timestamp: number | null | undefined): string | null {
  if (!timestamp) return null

  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return null

  // Return date in YYYY-MM-DD format
  return date.toISOString().split('T')[0]
}

export enum AttachmentColumns {
  CHECKBOX = 'checkbox',
  NAME = 'name',
  FILE_TYPE = 'fileType',
  FILE_CATEGORY = 'fileCategory',
  SOURCE = 'source',
  CATEGORY = 'category',
  FORM_TYPE = 'formType',
  WORKFLOW_TITLE = 'workflowTitle',
  WORKFLOW_STATUS = 'workflowStatus',
  LOCATION = 'location',
  DATE_UPLOADED = 'dateUploaded',
  UPLOADED_BY = 'uploadedBy',
  COMPANY = 'company',
}

const DEFAULT_COLUMN_WIDTH = 200
const NAME_COLUMN_WIDTH = 450
const CHECKBOX_COLUMN_WIDTH = 40

export default class AttachmentsStore extends BaseMultiBandListStore<IUploadLog> {
  @observable public workflows: Map<string, Workflow> = new Map()
  @observable public facets: any = null
  @observable public totalFacets: any = null
  @observable public hookUploadLogs: IUploadLog[] = []
  private dateFilterCallback?: (dates: {
    startDate?: number
    endDate?: number
  }) => void
  @observable public isMyAttachmentsFilter: boolean = false
  @observable public isMyCompanyAttachmentsFilter: boolean = false
  @observable public viewState: IViewState = {
    viewMode: ViewModes.List,
    sortingDataKey: '',
  }
  @observable public selectedDatePickerMode: TwoMonthsDatePickerMode =
    TwoMonthsDatePickerMode.FIXED_WEEK
  @observable public selectedAttachment: IUploadLog = null
  @observable public isAttachmentModalOpen: boolean = false
  @observable public displayedWorkflowId: string = null
  @observable public isPermitDialogDisplayed: boolean = false

  public dateFilters = observable<IDateFilters>({
    startDate: null,
    daysToAdd: 0,
    isDateFilterActive: true,
  })

  public constructor(
    private readonly eventsStore: DesktopEventStore,
    private readonly uploadLogsStore: UploadLogsStore,
    private readonly companiesStore: CompaniesStore,
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly userProjectsStore: UserProjectsStore,
    private readonly appState: DesktopInitialState,
    private readonly projectDateStore: ProjectDateStore,
    private readonly deliveriesStore: DeliveriesStore,
    private readonly uploadLogConfigurationStore: UploadLogConfigurationStore,
    private readonly workflowCategoriesStore: WorkflowCategoriesStore,
    private readonly deliveryDetailsStore: DeliveryDetailsStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
    private readonly locationAttributesStore: LocationAttributesStore,
  ) {
    super(
      eventsStore.appState.attachmentsFilters,
      () => uploadLogsStore.sortedList,
      [],
      'id',
    )

    makeObservable(this)

    Guard.requireAll({
      eventsStore,
      uploadLogsStore,
      companiesStore,
      projectMembersStore,
      userProjectsStore,
      appState,
      projectDateStore,
      deliveriesStore,
      uploadLogConfigurationStore,
      workflowCategoriesStore,
      deliveryDetailsStore,
      workflowTypesStore,
    })
  }

  public setWorkflows(workflows: Workflow[]) {
    runInAction(() => {
      this.workflows = new Map(
        workflows.map(workflow => [workflow.id, workflow]),
      )
    })
  }

  public get viewMode(): ViewModes {
    return this.viewState.viewMode
  }

  @computed
  public get viewModes(): IModeSettings[] {
    return [
      getModeSettings(ViewModes.List),
      getModeSettings(ViewModes.Map, true),
    ]
  }

  private get userProjectSettings(): UserProject {
    return this.userProjectsStore.getByUser(this.appState.user)
  }

  @action.bound
  public toggleMyAttachmentsFilter(): void {
    this.isMyAttachmentsFilter = !this.isMyAttachmentsFilter
  }

  @action.bound
  public toggleMyCompanyAttachmentsFilter(): void {
    this.isMyCompanyAttachmentsFilter = !this.isMyCompanyAttachmentsFilter
  }

  @action.bound
  public initialize(uploadLogsFromHook?: IUploadLog[]): void {
    this.setUploadLogs(uploadLogsFromHook || [])
    this.eventsStore.dispatch(e.GET_UPLOAD_LOG_CUSTOM_FILTERS)
    this.uploadLogConfigurationStore.getUploadLogConfiguration()
  }

  @action.bound
  public setUploadLogs(
    uploadLogs: IUploadLog[],
    workflows?: Map<string, Workflow>,
  ): void {
    this.hookUploadLogs = uploadLogs
    this.uploadLogsStore.receiveList(uploadLogs)
    if (workflows) {
      this.workflows = workflows
    }
  }

  @action.bound
  public setFacets(facets: any): void {
    this.facets = facets
  }

  @action.bound
  public setTotalFacets(facets: any): void {
    this.totalFacets = facets
  }

  @action.bound
  public setDateFilterCallback(
    callback: (dates: { startDate?: number; endDate?: number }) => void,
  ): void {
    this.dateFilterCallback = callback
    const endDate = this.projectDateStore.getEndDate(this.dateFilters)
    if (this.dateFilters.startDate && this.dateFilters.isDateFilterActive) {
      this.dateFilterCallback({
        startDate: this.dateFilters.startDate.getTime(),
        endDate: endDate.getTime(),
      })
    }
  }

  @action.bound
  public changeDateFilter(startDate?: number, endDate?: number): void {
    this.dateFilterCallback?.({ startDate, endDate })
  }

  public get attachmentsInPeriodInterval(): IUploadLog[] {
    if (this.selectedDatePickerMode === TwoMonthsDatePickerMode.PROJECT) {
      return this.hookUploadLogs
    }

    return this.hookUploadLogs.filter(log => {
      return this.isCreatedWithinRange(log.createdAt)
    })
  }

  public getFilteredCollectionExcludeFilter = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    excludedFilters: Array<string> = [],
  ) => {
    return this.instancesInPeriodInterval
  }

  protected createCategoryRow(categoryId: string, count: number): ILWFCRow {
    const categoryInstances = this.categoryToInstancesMap[categoryId]

    const category: ILWFCCategory = {
      categoryId,
      shortCategoryLabel: this.getCategoryLabelById(categoryId),
      categoryLabel: `${this.getCategoryLabelById(categoryId)} (${count})`,
      isChecked: categoryInstances.every(
        inst =>
          !this.canSelectInstance(inst) || this.selection.get(inst[this.idKey]),
      ),
    }

    return { category, data: {}, height: CATEGORY_ROW_HEIGHT, level: 0 }
  }

  public get isLoading(): boolean {
    return (
      this.hookUploadLogs.length === 0 && !this.uploadLogsStore.isDataReceived
    )
  }

  public downloadDocumentByLink = async (
    uploadLog: IUploadLog,
  ): Promise<void> => {
    try {
      const response = await fetch(uploadLog.fileUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`)
      }

      const blob = await response.blob()

      const objectUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = objectUrl
      link.download = this.getDisplayFileName(uploadLog)
      link.target = '_blank'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(objectUrl)
    } catch (error) {
      console.error('Download failed:', error)
      window.open(uploadLog.fileUrl, '_blank')
    }
  }

  protected get instancesInPeriodInterval(): IUploadLog[] {
    let filteredLogs = this.logsInPeriodInterval

    if (this.isMyAttachmentsFilter) {
      filteredLogs = filteredLogs.filter(
        log => log.userId === this.appState.user.id,
      )
    }

    if (this.isMyCompanyAttachmentsFilter) {
      const myCompanyId = this.userProjectSettings.companyId
      filteredLogs = filteredLogs.filter(
        log => log.userCompanyId === myCompanyId,
      )
    }

    return filteredLogs
  }

  @computed
  public get logsInPeriodInterval(): IUploadLog[] {
    if (this.selectedDatePickerMode === TwoMonthsDatePickerMode.PROJECT) {
      return this.hookUploadLogs
    }

    return this.hookUploadLogs.filter(log => {
      return this.isCreatedWithinRange(log.createdAt)
    })
  }

  public isCreatedWithinRange(date: Date | number): boolean {
    const { startOfDay, endOfDay } = this.projectDateStore
    const startDate = startOfDay(this.dateFilters.startDate)
    const endDate = endOfDay(this.endDate)
    if (!endDate || !startDate) {
      return true
    }
    return isBetween(startDate, endDate, new Date(date))
  }

  public get endDate(): Date {
    return this.projectDateStore.getEndDate(this.dateFilters)
  }

  @action.bound
  public dispose(): void {
    // Clean up any subscriptions or listeners
  }

  @action.bound
  public openAttachmentModal(attachment: IUploadLog): void {
    this.selectedAttachment = attachment
    this.isAttachmentModalOpen = true
  }

  @action.bound
  public closeAttachmentModal(): void {
    this.selectedAttachment = null
    this.isAttachmentModalOpen = false
  }

  @action.bound
  public openDeliveryDetailsPanel(deliveryId: string): void {
    const delivery = this.eventsStore.appState.deliveries.get(deliveryId)
    if (delivery) {
      this.deliveryDetailsStore.setViewMode(delivery)
    }
  }

  @action.bound
  public closeDeliveryDetailsPanel(): void {
    this.deliveryDetailsStore.displayedDelivery = null
  }

  public openFormDetailsPanel = (workflow: Workflow): void => {
    this.showPermitViewOrApprovalDialog(workflow)
  }

  @action.bound
  public closeFormDetailsPanel(): void {
    this.displayedWorkflowId = null
    this.isPermitDialogDisplayed = false
  }

  @action.bound
  private showPermitViewOrApprovalDialog(permit: any): void {
    this.displayedWorkflowId = permit?.id
    this.isPermitDialogDisplayed = true
  }

  @computed
  public get displayedWorkflow(): Workflow | null {
    if (!this.displayedWorkflowId) return null
    return this.workflows.get(this.displayedWorkflowId)
  }

  @action.bound
  public hidePermitDialog(): void {
    this.displayedWorkflowId = null
    this.isPermitDialogDisplayed = false
  }

  @action.bound
  public hideDeliverySideView(): void {
    this.deliveryDetailsStore.displayedDelivery = null
  }

  @computed
  public get isDataLoaded(): boolean {
    return this.uploadLogsStore.isDataReceived
  }

  @action.bound
  public setViewMode(viewMode: ViewModes): void {
    this.viewState.viewMode = viewMode
    this.appState.attachmentsList = {
      ...this.appState.attachmentsList,
      viewMode,
    }
  }

  @action.bound
  public changeDatePickerMode(mode: TwoMonthsDatePickerMode): void {
    this.selectedDatePickerMode = mode
  }

  public handleColumnSort(columnKey: string): void {
    if (this.sortState.columnKey === columnKey) {
      this.sortState.order =
        this.sortState.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
    } else {
      this.sortState.columnKey = columnKey
      this.sortState.order = SortOrder.DESC
    }
  }

  public get state(): DesktopInitialState {
    return this.appState
  }

  @observable
  public columnsWidthState: Map<string, number> = new Map<string, number>([
    [AttachmentColumns.CHECKBOX, CHECKBOX_COLUMN_WIDTH],
    [AttachmentColumns.NAME, NAME_COLUMN_WIDTH],
    [AttachmentColumns.FILE_TYPE, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.FILE_CATEGORY, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.SOURCE, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.CATEGORY, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.FORM_TYPE, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.WORKFLOW_TITLE, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.WORKFLOW_STATUS, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.LOCATION, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.DATE_UPLOADED, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.UPLOADED_BY, DEFAULT_COLUMN_WIDTH],
    [AttachmentColumns.COMPANY, DEFAULT_COLUMN_WIDTH],
  ])

  public getColumnWidth = (columnKey: string): number => {
    return this.columnsWidthState.get(columnKey) || DEFAULT_COLUMN_WIDTH
  }

  @computed
  public get defaultColumns(): ILWFCColumn[] {
    const FIXED_COLUMN_KEYS = [
      AttachmentColumns.CHECKBOX,
      AttachmentColumns.NAME,
      AttachmentColumns.FILE_CATEGORY,
    ]

    const columns = [
      {
        label: '',
        dataKey: AttachmentColumns.CHECKBOX,
        width: CHECKBOX_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.name,
        dataKey: AttachmentColumns.NAME,
        width: NAME_COLUMN_WIDTH,
        flexGrow: 1,
      },
      {
        label: Localization.translator.fileCategory,
        dataKey: AttachmentColumns.FILE_CATEGORY,
        width: DEFAULT_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.workflowCategory,
        dataKey: AttachmentColumns.CATEGORY,
        width: DEFAULT_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.workflowType,
        dataKey: AttachmentColumns.FORM_TYPE,
        width: DEFAULT_COLUMN_WIDTH,
      },

      {
        label: Localization.translator.workflowId,
        dataKey: AttachmentColumns.SOURCE,
        width: DEFAULT_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.workflowTitle,
        dataKey: AttachmentColumns.WORKFLOW_TITLE,
        width: DEFAULT_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.workflowStatus,
        dataKey: AttachmentColumns.WORKFLOW_STATUS,
        width: DEFAULT_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.location,
        dataKey: AttachmentColumns.LOCATION,
        width: DEFAULT_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.company,
        dataKey: AttachmentColumns.COMPANY,
        width: DEFAULT_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.uploadedBy,
        dataKey: AttachmentColumns.UPLOADED_BY,
        width: DEFAULT_COLUMN_WIDTH,
      },
      {
        label: Localization.translator.dateUploaded,
        dataKey: AttachmentColumns.DATE_UPLOADED,
        width: DEFAULT_COLUMN_WIDTH,
      },

      {
        label: Localization.translator.fileType,
        dataKey: AttachmentColumns.FILE_TYPE,
        width: DEFAULT_COLUMN_WIDTH,
        hidden: true,
      },
    ]

    return columns.map(column => ({
      ...column,
      width: this.columnsWidthState.get(column.dataKey),
      isHidden: column.hidden || false,
      isFixed: FIXED_COLUMN_KEYS.includes(column.dataKey),
    }))
  }

  public get isConfigurationLoading(): boolean {
    return this.uploadLogConfigurationStore.isLoading
  }

  @computed
  public get columns(): ILWFCColumn[] {
    const columnsConfig = this.uploadLogConfiguration?.columnsConfig || []

    if (!columnsConfig?.length) {
      return this.defaultColumns
    }

    const mappedConfigCols = columnsConfig.reduce((list, columnSorted) => {
      const column = this.defaultColumns.find(
        _ => _.dataKey == columnSorted.columnId,
      )
      if (column) {
        list.push({
          ...column,
          width: this.columnsWidthState.get(column.dataKey),
          isHidden: columnSorted.isHidden,
          isFixed: columnSorted.isFixed,
        })
      }
      return list
    }, [] as ILWFCColumn[])

    const absentColumns = this.defaultColumns.filter(
      col => !mappedConfigCols.some(config => config.dataKey === col.dataKey),
    )
    absentColumns.forEach(col =>
      mappedConfigCols.push({ ...col, isFixed: false }),
    )

    return mappedConfigCols
  }

  @computed
  public get fixedColumnsCount() {
    return this.columns.filter(c => c.isFixed).length
  }

  private get uploadLogConfiguration(): IUploadLogConfiguration {
    return this.uploadLogConfigurationStore.configuration
  }

  @action.bound
  public resetColumnsConfiguration(): void {
    this.uploadLogConfiguration.columnsConfig = this.mapColumnsToConfig(
      this.defaultColumns,
    )

    this.saveUploadLogColumnConfiguration()
  }

  @action.bound
  public updateColumnsConfiguration(collection: ILWFCColumn[]): void {
    this.uploadLogConfiguration.columnsConfig =
      this.mapColumnsToConfig(collection)

    this.saveUploadLogColumnConfiguration()
  }

  private mapColumnsToConfig(
    collection: ILWFCColumn[],
  ): IColumnConfiguration[] {
    return collection.map(column => ({
      columnId: column.dataKey,
      isHidden: column.isHidden,
      isFixed: column.isFixed,
    }))
  }

  private saveUploadLogColumnConfiguration = () => {
    const uploadLogConfiguration = {
      ...this.uploadLogConfiguration,
      projectId: this.state.activeProject.id,
    }
    this.uploadLogConfigurationStore.saveUploadLogConfiguration(
      uploadLogConfiguration,
    )
  }

  public get rows(): ILWFCRow[] {
    const { groupingKey } = this.filter

    const customSortTreeObjs =
      groupingKey === AttachmentGroupingOption.Date
        ? this.sortDateTreeObjs
        : this.defaultSortTreeObjs

    return [
      { data: null },
      ...this.toBandTreeNodeRows(
        [groupingKey],
        null,
        '',
        0,
        false,
        customSortTreeObjs,
      ),
    ]
  }

  private defaultSortTreeObjs = (treeNodeObjs: ITreeNodeObj[]) => {
    const map = new Map<string, ITreeNodeObj>()
    treeNodeObjs.forEach(obj => map.set(obj.id, obj))

    const unassignedValues = [UNASSIGNED, NO_SPECIFIED]

    return Array.from(map.values()).sort((a, b) => {
      if (unassignedValues.includes(a.id)) return 1
      if (unassignedValues.includes(b.id)) return -1

      return a.name?.localeCompare(b.name)
    })
  }

  private sortDateTreeObjs = (treeNodeObjs: ITreeNodeObj[]) => {
    const map = new Map<string, ITreeNodeObj>()
    treeNodeObjs.forEach(obj => map.set(obj.id, obj))

    return Array.from(map.values()).sort((a, b) => {
      if (a.id === UNASSIGNED) return 1
      if (b.id === UNASSIGNED) return -1

      return b.id.localeCompare(a.id)
    })
  }

  protected getTreeNodeObjsByBand(band: string): ITreeNodeObj[] {
    const { getMonthDayAndYearToDisplay } = this.projectDateStore

    switch (band) {
      case AttachmentGroupingOption.None:
        return [
          {
            id: AttachmentGroupingOption.None,
            name: Localization.translator.none,
          },
        ]
      case AttachmentGroupingOption.Date:
        const uniqueDates = new Map<string, number>()
        this.filteredCollection.forEach(uploadLog => {
          const dateKey = getDateKey(uploadLog.createdAt)
          if (dateKey) {
            if (!uniqueDates.has(dateKey)) {
              uniqueDates.set(dateKey, uploadLog.createdAt)
            }
          } else {
            uniqueDates.set(UNASSIGNED, null)
          }
        })

        return Array.from(uniqueDates.entries()).map(([dateKey, timestamp]) => {
          if (dateKey === UNASSIGNED) {
            return {
              name: UNASSIGNED,
              id: UNASSIGNED,
            }
          }
          return {
            name: getMonthDayAndYearToDisplay(timestamp) || dateKey,
            id: dateKey,
          }
        })
      case AttachmentGroupingOption.Company:
        return [
          ...this.filteredCollection
            .flatMap(uploadLog => uploadLog.userCompanyId)
            .map(companyId => {
              const company = this.companiesStore.getCompanyById(companyId)
              return {
                name: company?.name || UNASSIGNED,
                id: company?.id || UNASSIGNED,
                object: {
                  icon: Avatar,
                  iconProps: {
                    src: company?.avatarUrl,
                    size: AvatarSize.Minuscule,
                  },
                },
              }
            }),
          groupingCommonMapper(UNASSIGNED),
        ]
      case AttachmentGroupingOption.UploadedBy:
        return this.projectMembersStore
          .getByIds(this.filteredCollection.map(uploadLog => uploadLog.userId))
          .map(member => ({
            name: member.fullName || UNASSIGNED,
            id: member.id || UNASSIGNED,
            object: {
              icon: Avatar,
              iconProps: {
                src: member?.avatarUrl,
                size: AvatarSize.Minuscule,
              },
            },
          }))
      case AttachmentGroupingOption.Location:
        const uniqueLocations = new Map<
          string,
          { name: string; locationBase: LocationBase | null }
        >()
        this.filteredCollection.forEach(uploadLog => {
          const currentLocationId = this.getUploadLogLocationId(uploadLog)

          if (currentLocationId) {
            const location =
              this.locationAttributesStore.getById(currentLocationId)
            if (!location) {
              uniqueLocations.set(UNASSIGNED, {
                name: UNASSIGNED,
                locationBase: null,
              })
            } else {
              uniqueLocations.set(currentLocationId, {
                name: location.name,
                locationBase: location,
              })
            }
          } else if (uploadLog.locationName) {
            uniqueLocations.set(uploadLog.locationName, {
              name: uploadLog.locationName,
              locationBase: null,
            })
          } else {
            uniqueLocations.set(UNASSIGNED, {
              name: UNASSIGNED,
              locationBase: null,
            })
          }
        })

        return Array.from(uniqueLocations.entries()).map(
          ([id, locationData]) => ({
            name: locationData.name,
            id,
            object: locationData.locationBase,
          }),
        )
      case AttachmentGroupingOption.FormType:
        return this.filteredCollection.map(uploadLog => {
          const workflow = this.workflows.get(uploadLog.sourceEntityId)
          const formType = workflow?.getTypeOfPermitType(
            this.workflowTypesStore,
          )
          const formTypeName =
            workflow?.getNameOfPermitType(this.workflowTypesStore) ||
            uploadLog.sourceEntityTypeName
          const groupKey = formTypeName || UNASSIGNED
          return {
            name: groupKey,
            id: formType || UNASSIGNED,
            object: {
              icon: WorkflowTypeIcon,
              iconProps: {
                iconName: workflow?.getIconNameOfPermitType(
                  this.workflowTypesStore,
                ),
                className: 'icon-20',
              },
            },
          }
        })
      case AttachmentGroupingOption.FileType:
        const uniqueFileTypes = new Map<string, string>()
        this.filteredCollection.forEach(uploadLog => {
          if (uploadLog.fileType) {
            const processedFileType = getAttachmentFileType(
              uploadLog.fileType,
              '/',
              true,
            )
            uniqueFileTypes.set(processedFileType, processedFileType)
          } else {
            uniqueFileTypes.set(UNASSIGNED, UNASSIGNED)
          }
        })

        return Array.from(uniqueFileTypes.entries()).map(
          ([fileType, displayName]) => ({
            name: displayName.toUpperCase(),
            id: fileType,
            object: {
              icon: FileTypeIcon,
              iconProps: {
                fileType: fileType,
              },
            },
          }),
        )
      case AttachmentGroupingOption.WorkflowCategory:
        const uniqueCategoryIds = new Set<{
          categoryId: string
          isDefault: boolean
        }>()
        this.filteredCollection.forEach(uploadLog => {
          if (uploadLog.sourceEntityType === SourceEntityType.Form) {
            const workflow = this.workflows.get(uploadLog.sourceEntityId)
            const isDefault = this.workflowCategoriesStore.getById(
              workflow?.categoryId,
            )?.isDefaultCategory
            if (workflow?.categoryId) {
              uniqueCategoryIds.add({
                categoryId: workflow.categoryId,
                isDefault: isDefault,
              })
            } else {
              uniqueCategoryIds.add({
                categoryId: UNASSIGNED,
                isDefault: false,
              })
            }
          } else {
            uniqueCategoryIds.add({
              categoryId: UNASSIGNED,
              isDefault: false,
            })
          }
        })

        return Array.from(uniqueCategoryIds).map(
          ({ categoryId, isDefault }) => ({
            name:
              categoryId === UNASSIGNED
                ? UNASSIGNED
                : this.workflowCategoriesStore.getNameById(categoryId),
            id: categoryId,
            object: {
              icon: isDefault ? Icons.LogoDarkSmall : null,
              infoTooltip: isDefault
                ? Localization.translator.categoryCreatedByStruxHub
                : null,
            },
          }),
        )
      case AttachmentGroupingOption.WorkflowStatus:
        const uniqueStatuses = new Set<string>()
        this.filteredCollection.forEach(uploadLog => {
          if (uploadLog.sourceEntityType === SourceEntityType.Form) {
            const workflow = this.workflows.get(uploadLog.sourceEntityId)
            uniqueStatuses.add(workflow?.status || UNASSIGNED)
          } else {
            uniqueStatuses.add(UNASSIGNED)
          }
        })

        return Array.from(uniqueStatuses, statusId => ({
          name:
            statusId === UNASSIGNED
              ? UNASSIGNED
              : getFormStatusDisplayName(statusId as SitePermitStatus),
          id: statusId,
          object:
            statusId !== UNASSIGNED
              ? {
                  labelNode: React.createElement(StatusBadge, {
                    status: statusId as SitePermitStatus,
                  }),
                }
              : undefined,
        }))
      case AttachmentGroupingOption.FileCategory:
        return this.filteredCollection
          .map(uploadLog =>
            getFileTypeToCategory(
              getAttachmentFileType(uploadLog.fileType, '/'),
            ),
          )
          .map(fileCategory => ({
            name: (fileCategory || UNASSIGNED).toUpperCase(),
            id: fileCategory || UNASSIGNED,
            object: {
              icon: FileCategoryIcon,
              iconProps: {
                fileCategory: fileCategory,
              },
            },
          }))
      default:
        return []
    }
  }

  @computed
  protected get bandObjectMap(): {
    [bandType: string]: { [groupName: string]: IUploadLog[] }
  } {
    const map = {}

    const groupingOptions = Object.values(AttachmentGroupingOption)
    groupingOptions.forEach(option => {
      const bandMap = (map[option] = {})

      // Standard grouping logic for all options including dates
      this.filteredCollection.forEach(uploadLog => {
        const groupNames: string[] = this.getCategoryIds(uploadLog, option)
        if (!groupNames.length) {
          groupNames.push(UNASSIGNED)
        }

        groupNames.forEach(groupName => {
          if (!bandMap[groupName]) {
            bandMap[groupName] = []
          }

          bandMap[groupName].push(uploadLog)
        })
      })
    })

    Object.keys(map).forEach(groupName => {
      Object.keys(map[groupName]).forEach(subGroupName => {
        if (this.shouldSort) {
          const rows = this.toRows(map[groupName][subGroupName])
          rows.sort(this.compareRows)
          map[groupName][subGroupName] = rows
            .map(row => {
              const uploadLogId = row.data.id
              return map[groupName][subGroupName].find(
                uploadLog => uploadLog.id === uploadLogId,
              )
            })
            .filter(Boolean)
        } else {
          map[groupName][subGroupName].sort((a, b) => {
            const dateA = a.createdAt || 0
            const dateB = b.createdAt || 0
            return dateB - dateA
          })
        }
      })
    })

    return map
  }

  /**
   * The location an upload is grouped under: its workflow's first location, else the
   * first of the upload's own locationIds. An id only - the kind stored beside it on the
   * workflow value or the upload is a snapshot that goes stale on a re-kind (STX1-597).
   */
  private getUploadLogLocationId(uploadLog: IUploadLog): string | undefined {
    const workflow = this.workflows.get(uploadLog.sourceEntityId)

    return (
      workflow?.locations?.[0]?.id || uploadLog.locationIds?.[0] || undefined
    )
  }

  private getCategoryIds(uploadLog: IUploadLog, band: string) {
    switch (band) {
      case AttachmentGroupingOption.None:
        return [AttachmentGroupingOption.None]
      case AttachmentGroupingOption.Company:
        return [uploadLog.userCompanyId || UNASSIGNED]
      case AttachmentGroupingOption.Date:
        const dateKey = getDateKey(uploadLog.createdAt)
        return [dateKey || UNASSIGNED]
      case AttachmentGroupingOption.UploadedBy:
        return [uploadLog.userId || UNASSIGNED]
      case AttachmentGroupingOption.Location:
        const currentLocationId = this.getUploadLogLocationId(uploadLog)

        return currentLocationId &&
          this.locationAttributesStore.getById(currentLocationId)
          ? [currentLocationId]
          : [UNASSIGNED]
      case AttachmentGroupingOption.FormType:
        const workflow = this.workflows.get(uploadLog.sourceEntityId)
        const workflowType =
          workflow?.getTypeOfPermitType(this.workflowTypesStore) ||
          uploadLog.sourceEntityTypeName
        return [workflowType || UNASSIGNED]
      case AttachmentGroupingOption.FileType:
        return uploadLog.fileType
          ? [getAttachmentFileType(uploadLog.fileType, '/', true)]
          : [UNASSIGNED]
      case AttachmentGroupingOption.WorkflowCategory:
        if (uploadLog.sourceEntityType === SourceEntityType.Form) {
          const workflow = this.workflows.get(uploadLog.sourceEntityId)
          return [workflow?.categoryId || UNASSIGNED]
        }
        return [UNASSIGNED]
      case AttachmentGroupingOption.WorkflowStatus:
        if (uploadLog.sourceEntityType === SourceEntityType.Form) {
          const workflow = this.workflows.get(uploadLog.sourceEntityId)
          return [workflow?.status || UNASSIGNED]
        }
        return [UNASSIGNED]
      case AttachmentGroupingOption.FileCategory:
        return [
          getFileTypeToCategory(
            getAttachmentFileType(uploadLog.fileType, '/'),
          ) || UNASSIGNED,
        ]
      default:
        return [UNASSIGNED]
    }
  }

  protected toRows(uploadLogs: IUploadLog[]): ILWFCRow[] {
    return uploadLogs.map(uploadLog => ({
      data: {
        id: uploadLog.id,
        [AttachmentColumns.CHECKBOX]: false,
        [AttachmentColumns.NAME]: {
          uploadLog,
          fileName: this.getDisplayFileName(uploadLog),
          fileType: getAttachmentFileType(uploadLog.fileType, '/', true),
        },
        [AttachmentColumns.FILE_TYPE]: getAttachmentFileType(
          uploadLog.fileType,
          '/',
          true,
        ),
        [AttachmentColumns.FILE_CATEGORY]: getFileTypeToCategory(
          getAttachmentFileType(uploadLog.fileType, '/'),
        ),
        [AttachmentColumns.SOURCE]: this.getSourceDisplay(uploadLog),
        [AttachmentColumns.CATEGORY]: uploadLog,
        [AttachmentColumns.FORM_TYPE]: uploadLog,
        [AttachmentColumns.WORKFLOW_TITLE]: uploadLog,
        [AttachmentColumns.WORKFLOW_STATUS]: uploadLog,
        [AttachmentColumns.LOCATION]: uploadLog,
        [AttachmentColumns.DATE_UPLOADED]: uploadLog.createdAt,
        [AttachmentColumns.UPLOADED_BY]: uploadLog,
        [AttachmentColumns.COMPANY]: uploadLog,
      },
    }))
  }

  public getDisplayFileName(uploadLog: IUploadLog): string {
    if (uploadLog.fileUrl) {
      const firstUnderscoreIndex = uploadLog.fileUrl.indexOf('_')
      const questionMarkIndex = uploadLog.fileUrl.indexOf('?')
      const fileName = uploadLog.fileUrl
        .slice(firstUnderscoreIndex + 1, questionMarkIndex)
        .replace(/%20/g, ' ')
      return fileName
    }
    return uploadLog.sourceEntityCode || `Upload-${uploadLog.id?.slice(-8)}`
  }

  public getSourceDisplay(uploadLog: IUploadLog): string {
    const sourceType = uploadLog.sourceEntityType || ''
    const sourceCode = this.getSourceCode(uploadLog)

    return sourceCode || sourceType
  }

  private getSourceCode(uploadLog: IUploadLog): string {
    if (uploadLog.sourceEntityCode) {
      return uploadLog.sourceEntityCode
    }
    if (uploadLog.sourceEntityType === SourceEntityType.Form) {
      return this.workflows.get(uploadLog.sourceEntityId)?.code
    }
    return this.deliveriesStore.byId.get(uploadLog.sourceEntityId)?.bookingId
  }

  @computed
  protected get categoryToInstancesMap(): {
    [categoryId: string]: IUploadLog[]
  } {
    const map = {}

    map[AttachmentsSourceGroupingOption.FORM_WORKFLOWS] = []
    map[AttachmentsSourceGroupingOption.MAPS_SETUP] = []

    this.filteredCollection.forEach(uploadLog => {
      const sourceType = uploadLog.sourceEntityType

      if (
        sourceType === SourceEntityType.Form ||
        sourceType === SourceEntityType.Delivery
      ) {
        map[AttachmentsSourceGroupingOption.FORM_WORKFLOWS].push(uploadLog)
      } else {
        map[AttachmentsSourceGroupingOption.MAPS_SETUP].push(uploadLog)
      }
    })

    return map
  }

  protected searchFiltering = (uploadLog: IUploadLog) => {
    const key = this.filter.searchKey.toLowerCase()
    const entityValues: string[] = [
      uploadLog.sourceEntityCode,
      uploadLog.locationName,
      ...(uploadLog.fileUrl ? [this.getDisplayFileName(uploadLog)] : []),
    ]
    return entityValues.some(value => value?.toLowerCase().includes(key))
  }

  protected compareRows = (row1: ILWFCRow, row2: ILWFCRow): number => {
    if (!this.shouldSort) return 0

    const { columnKey, order } = this.sortState
    const data1 = row1.data
    const data2 = row2.data

    let value1: any
    let value2: any

    switch (columnKey) {
      case AttachmentSortingOption.NAME:
        value1 = data1[AttachmentColumns.NAME]?.fileName || ''
        value2 = data2[AttachmentColumns.NAME]?.fileName || ''
        break
      case AttachmentSortingOption.FILE_TYPE:
        value1 = data1[AttachmentColumns.FILE_TYPE] || ''
        value2 = data2[AttachmentColumns.FILE_TYPE] || ''
        break
      case AttachmentSortingOption.FILE_CATEGORY:
        value1 = data1[AttachmentColumns.FILE_CATEGORY] || ''
        value2 = data2[AttachmentColumns.FILE_CATEGORY] || ''
        break
      case AttachmentSortingOption.SOURCE:
        value1 = data1[AttachmentColumns.SOURCE] || ''
        value2 = data2[AttachmentColumns.SOURCE] || ''
        break
      case AttachmentSortingOption.FORM_TYPE:
        const workflow1 = this.workflows.get(
          data1[AttachmentColumns.FORM_TYPE]?.sourceEntityId,
        )
        const workflow2 = this.workflows.get(
          data2[AttachmentColumns.FORM_TYPE]?.sourceEntityId,
        )
        value1 =
          workflow1?.getNameOfPermitType(this.workflowTypesStore) ||
          data1[AttachmentColumns.FORM_TYPE]?.sourceEntityTypeName ||
          ''
        value2 =
          workflow2?.getNameOfPermitType(this.workflowTypesStore) ||
          data2[AttachmentColumns.FORM_TYPE]?.sourceEntityTypeName ||
          ''
        break
      case AttachmentSortingOption.WORKFLOW_TITLE:
        const titleWorkflow1 = this.workflows.get(
          data1[AttachmentColumns.WORKFLOW_TITLE]?.sourceEntityId,
        )
        const titleWorkflow2 = this.workflows.get(
          data2[AttachmentColumns.WORKFLOW_TITLE]?.sourceEntityId,
        )
        value1 = titleWorkflow1?.title || ''
        value2 = titleWorkflow2?.title || ''
        break
      case AttachmentSortingOption.WORKFLOW_STATUS:
        const statusWorkflow1 = this.workflows.get(
          data1[AttachmentColumns.WORKFLOW_STATUS]?.sourceEntityId,
        )
        const statusWorkflow2 = this.workflows.get(
          data2[AttachmentColumns.WORKFLOW_STATUS]?.sourceEntityId,
        )
        value1 = statusWorkflow1?.status || ''
        value2 = statusWorkflow2?.status || ''
        break
      case AttachmentSortingOption.FORM_CATEGORY:
        const categoryWorkflow1 = this.workflows.get(
          data1[AttachmentColumns.CATEGORY]?.sourceEntityId,
        )
        const categoryWorkflow2 = this.workflows.get(
          data2[AttachmentColumns.CATEGORY]?.sourceEntityId,
        )
        value1 =
          categoryWorkflow1?.getCategoryCaption(this.workflowCategoriesStore) ||
          ''
        value2 =
          categoryWorkflow2?.getCategoryCaption(this.workflowCategoriesStore) ||
          ''
        break
      case AttachmentSortingOption.LOCATION:
        value1 = data1[AttachmentColumns.LOCATION]?.locationName || ''
        value2 = data2[AttachmentColumns.LOCATION]?.locationName || ''
        break
      case AttachmentSortingOption.DATE_UPLOADED:
        value1 = new Date(data1[AttachmentColumns.DATE_UPLOADED] || 0).getTime()
        value2 = new Date(data2[AttachmentColumns.DATE_UPLOADED] || 0).getTime()
        break
      case AttachmentSortingOption.UPLOADED_BY:
        const user1 = this.projectMembersStore.getById(
          data1[AttachmentColumns.UPLOADED_BY]?.userId || '',
        )
        const user2 = this.projectMembersStore.getById(
          data2[AttachmentColumns.UPLOADED_BY]?.userId || '',
        )
        value1 = user1?.fullName || ''
        value2 = user2?.fullName || ''
        break
      case AttachmentSortingOption.COMPANY:
        const company1 = this.companiesStore.getCompanyById(
          data1[AttachmentColumns.COMPANY]?.userCompanyId || '',
        )
        const company2 = this.companiesStore.getCompanyById(
          data2[AttachmentColumns.COMPANY]?.userCompanyId || '',
        )
        value1 = company1?.name || ''
        value2 = company2?.name || ''
        break
      default:
        return 0
    }

    let comparison = 0
    if (typeof value1 === 'number' && typeof value2 === 'number') {
      comparison = value1 - value2
    } else {
      const str1 = String(value1).toLowerCase()
      const str2 = String(value2).toLowerCase()
      comparison = str1.localeCompare(str2)
    }

    return order === SortOrder.ASC ? comparison : -comparison
  }
}

export function getFileTypeToCategory(type: FileType | string): FileCategory {
  switch (type) {
    case FileType.Image:
      return FileCategory.Image
    case FileType.Pdf:
      return FileCategory.Document
    case FileType.Video:
      return FileCategory.Video
    case FileType.Word:
      return FileCategory.Document
    case FileType.Excel:
      return FileCategory.Spreadsheet
    case FileType.PowerPoint:
      return FileCategory.Presentation
    default:
      return FileCategory.Image
  }
}
